import React, { Fragment } from 'react';
import { isNull } from 'lodash';
import cx from 'classnames';

import ProductDetailPanel from '../../ProductDetailPanel';
import BaseForm from '../../../../../components/form/BaseForm';
import InputForm from '../../../../../components/form/InputForm';
import Button from '../../../../../components/Button';
import CheckBox from '../../../../../components/input/CheckBox';
import Select from '../../../../../components/input/Select';
import { setDash } from '../../../../../lib/common';
import { compareProductVersion } from '../../../../../lib/utils';
import {
  DIRECT_DELIVERY_TIME_LIMIT,
  ORDER_MADE_DATE_SELECT_LIST,
  PRODUCT_DELIVERY_TYPE,
  PRODUCT_DELIVERY_TYPE_SELECT_LIST,
  PRODUCT_DETAIL_PAGE_PANEL_NAME,
} from '../../../../../constants/service/app/productConstants';
import './ProductDeliveryInfo.scss';

const ProductDeliveryInfo = (props) => {
  const { isEditable, form, initialValue, isCompare, comparisonData, ...res } = props;
  const { originAddress, returnAddress } = initialValue;
  const { deliveryType, isOrderMade, orderMadeDate, directDeliveryTimeLimit } = form.getFieldsValue();

  return (
    <ProductDetailPanel
      name={PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_DELIVERY_INFO}
      className="product-delivery-info"
      title="배송 정보"
      isCompare={isCompare}
      {...res}
    >
      <div className="panel-body">
        <BaseForm
          className={cx(
            'select-delivery-type-form',
            isCompare &&
              compareProductVersion(
                [deliveryType, isOrderMade, orderMadeDate, directDeliveryTimeLimit],
                [
                  comparisonData.deliveryType,
                  comparisonData.isOrderMade,
                  comparisonData.orderMadeDate,
                  comparisonData.directDeliveryTimeLimit,
                ],
              ),
          )}
          label="배송 타입"
          content={
            <div>
              <div className="select-delivery-type">
                <div className={cx({ required: isNull(deliveryType) })}>
                  {PRODUCT_DELIVERY_TYPE_SELECT_LIST.map((item) => (
                    <Button
                      type="button"
                      key={item.id}
                      color={deliveryType === item.id ? 'primary' : 'light'}
                      text={item.name}
                      onClick={() => isEditable && form.setFieldsValue({ deliveryType: item.id })}
                    />
                  ))}
                </div>
                {isNull(deliveryType) && <div className="form-explain">필수 정보입니다.</div>}
              </div>

              {/* 배송 타입이 일반 배송인 경우(0)*/}
              {deliveryType === PRODUCT_DELIVERY_TYPE.GENERAL_DELIVERY && (
                <div className="general-delivery-type">
                  <div className="sub-info">일반 배송의 경우, "평균 3일 내 배송 (주말/공휴일 제외)"로 표시됩니다.</div>
                  <CheckBox
                    checked={isOrderMade}
                    disabled={!isEditable}
                    onChange={(isOrderMade) => form.setFieldsValue({ isOrderMade })}
                    text={
                      <span>
                        주문 시 제작
                        <span className="sub-info">"입력 기한 + 배송에 2일 소요 (주말/공휴일 제외)"로 표시됩니다.</span>
                      </span>
                    }
                  />

                  {/* 주문 시 제작인 상품인 경우에만 주문 시 제작 소요시간을 선택함*/}
                  {isOrderMade && (
                    <div className="order-made-date">
                      <div className="select-order-made-date-info">
                        제작 후 <span>발송</span>까지 약
                        <Select
                          list={ORDER_MADE_DATE_SELECT_LIST}
                          disabled={!isEditable}
                          onChange={(orderMadeDate) => form.setFieldsValue({ orderMadeDate })}
                          value={orderMadeDate}
                        />
                        일이 소요 됩니다. (영업일 기준으로 최대한 정확하게 적어주세요.)
                      </div>
                      <div className="sub-info">
                        <div>*주문제작 상품의 경우, 발송까지의 기간을 최대 14일로 지정할 수 있습니다.</div>
                        <div>*주말/공휴일을 제외한 영업일 기준으로 입력해주세요.</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 배송 타입이 바로 배송인 경우(1)*/}
              {deliveryType === PRODUCT_DELIVERY_TYPE.DIRECT_DELIVERY && (
                <div className="direct-delivery-type">
                  <span>기준 시간 설정</span>
                  <Select
                    list={DIRECT_DELIVERY_TIME_LIMIT}
                    disabled={!isEditable}
                    onChange={(directDeliveryTimeLimit) => form.setFieldsValue({ directDeliveryTimeLimit })}
                    value={directDeliveryTimeLimit}
                  />

                  <div className="sub-info">
                    <div>*옵션까지 모두 바로 배송이 가능한 상품만 체크해주세요.</div>
                    <div>
                      *바로 배송 상품은 설정한 시간 전까지 고객이 상품을 결제 완료할 경우, 당일에 배송 처리하는
                      서비스입니다.
                    </div>
                    <div>*바로 배송 설정 후, 당일 날 배송 처리를 진행하지 않으면 불이익이 있습니다.</div>
                    <div>
                      *바로 배송 상품은 앱 내에서 상단 노출 혜택을 드리며, 홈 화면에 바로 배송 상품만을 위한 공간을
                      만들어드립니다.
                    </div>
                  </div>
                </div>
              )}
            </div>
          }
        />

        {/* 출고지 */}
        <BaseForm
          label="출고지"
          content={
            <Fragment>
              <div>{`(${setDash(originAddress.postCode)}) ${setDash(originAddress.line1)} `}</div>
              <div>{setDash(originAddress.line2)}</div>
            </Fragment>
          }
        />

        {/* 추가 배송정보 */}
        <BaseForm
          label="추가 배송정보"
          content={setDash(originAddress.memo)}
          subContent={
            <div className="sub-info">
              <div>(지역별 차등 배송비 정보)</div>
            </div>
          }
        />

        {/* 반품/교환지 */}
        <BaseForm
          label="반품/교환지"
          content={
            <Fragment>
              <div>{`(${setDash(returnAddress.postCode)}) ${setDash(returnAddress.line1)} `}</div>
              <div>{setDash(returnAddress.line2)}</div>
            </Fragment>
          }
        />

        {/* 반품/교환지 정보 */}
        <BaseForm label="반품/교환 정보" content={setDash(returnAddress.memo)} />
      </div>
      <div className="hidden-form">
        <InputForm form={form} type="hidden" name="deliveryType" initialValue={initialValue.deliveryType} />
        <InputForm form={form} type="hidden" name="isOrderMade" initialValue={initialValue.isOrderMade} />
        <InputForm form={form} type="hidden" name="orderMadeDate" initialValue={initialValue.orderMadeDate} />
        <InputForm
          form={form}
          type="hidden"
          name="directDeliveryTimeLimit"
          initialValue={initialValue.directDeliveryTimeLimit}
        />
      </div>
    </ProductDetailPanel>
  );
};

export default ProductDeliveryInfo;
